import moment from 'moment'

export class StateManager {
  constructor (controller, SceneCollectionService) {
    this.controller = controller
    this.SceneCollectionService = SceneCollectionService

    // State
    this.availableSceneCollections = []
    this.creatingInterval = null
    this.editingEvent = null
    this.pendingIntervalData = null
  }

  /**
     * Load available scene collections for current scene
     * @returns {Promise} Scene collections data
     */
  loadAvailableSceneCollections () {
    if (!this.controller.sceneId || !this.controller.sceneId.id) {
      this.controller._$log.error('[ScheduleCalendar] No scene ID available for loading collections')
      return Promise.reject(new Error('Scene ID required'))
    }

    return this.SceneCollectionService.getSceneCollectionsForScene(this.controller.sceneId.id)
      .then(sceneCollections => {
        this.availableSceneCollections = sceneCollections || []
        this.controller._$log.info('[ScheduleCalendar] Loaded scene collections:', this.availableSceneCollections.length)
        return this.availableSceneCollections
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Failed to load scene collections:', error)
        this.availableSceneCollections = []
        throw error
      })
  }

  /**
     * Create new interval with selected scene collection
     */
  createInterval (interval) {
    if (interval) {
      this.creatingInterval = interval
    }
    if (!this.creatingInterval || !this.creatingInterval.selectedSceneCollectionId) {
      this.controller._$log.warn('[ScheduleCalendar] Cannot create interval - missing data')
      return
    }

    const intervalData = {
      sceneCollectionId: parseInt(this.creatingInterval.selectedSceneCollectionId, 10),
      interval_start: this.creatingInterval.startTime.format(), // ISO 8601 with timezone
      interval_end: this.creatingInterval.endTime.format(), // ISO 8601 with timezone
      priority: parseInt(this.creatingInterval.priority, 10) || 100
    }

    this.controller._$log.info('[ScheduleCalendar] Creating new interval:', intervalData)

    // Validate required fields
    if (!intervalData.sceneCollectionId || isNaN(intervalData.sceneCollectionId)) {
      this.controller._$log.error('[ScheduleCalendar] Invalid sceneCollectionId:', intervalData.sceneCollectionId)
      window.alert('Please select a valid scene collection before creating the interval.')
      return
    }

    // Check for overlaps before creating the interval
    // Use ConflictManager from controller
    this.controller.conflictManager.checkForOverlaps(
      this.creatingInterval.startTime.toDate(),
      this.creatingInterval.endTime.toDate(),
      null // no exclude since this is a new interval
    ).then(overlappingEvents => {
      if (overlappingEvents.length > 0) {
        // Found overlaps - show conflict resolution modal
        this.controller._$log.info('[ScheduleCalendar] Found overlaps when creating interval, showing conflict modal:', {
          overlappingCount: overlappingEvents.length,
          newPriority: intervalData.priority
        })

        // Create a temporary event object for the conflict system
        const tempEvent = {
          start: this.creatingInterval.startTime.toDate(),
          end: this.creatingInterval.endTime.toDate(),
          extendedProps: {
            intervalId: null, // new interval
            sceneCollectionId: intervalData.sceneCollectionId,
            priority: intervalData.priority
          }
        }

        // Store the interval data for later use in conflict resolution
        this.pendingIntervalData = intervalData

        // Close create modal first so conflict modal is visible
        this.controller.closeCreateIntervalModal()

        // Show overlap dialog via ConflictManager
        this.controller.conflictManager.showOverlapDialog(tempEvent, overlappingEvents, {
          revert: () => {
            // Clear pending data if user cancels
            this.pendingIntervalData = null
          }
        })
      } else {
        // No overlaps - proceed with creation
        this.proceedWithIntervalCreation(intervalData)
      }
    }).catch(error => {
      this.controller._$log.error('[ScheduleCalendar] Error checking for overlaps:', error)
      // Proceed anyway if overlap check fails
      this.proceedWithIntervalCreation(intervalData)
    })
  }

  /**
     * Proceed with interval creation (after conflict resolution or no conflicts)
     */
  proceedWithIntervalCreation (intervalData) {
    this.SceneCollectionService.addInterval(intervalData)
      .then(response => {
        this.controller._$log.info('[ScheduleCalendar] Interval created successfully:', response)

        // Refresh calendar events to show new interval
        if (this.controller.calendarManager && this.controller.calendarManager.calendar) {
          this.controller.calendarManager.calendar.refetchEvents()
          // Refresh conflict styling after new interval is created
          this.controller._$timeout(() => {
            this.controller.refreshAllEventStyles()
          }, 0)
        }

        // Close modal and clear pending data
        this.controller.closeCreateIntervalModal()
        this.pendingIntervalData = null
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Failed to create interval:', error)
        window.alert('Failed to create interval: ' + (error.message || 'Unknown error. Please try again.'))
      })
  }

  /**
     * Save event edit changes
     */
  saveEventEdit (event) {
    if (event) {
      this.editingEvent = event
    }
    if (!this.editingEvent) {
      return
    }

    // Extract date and time from Date objects
    const startDateMoment = moment(this.editingEvent.startDate)
    const startTimeMoment = moment(this.editingEvent.startTime)
    const endDateMoment = moment(this.editingEvent.endDate)
    const endTimeMoment = moment(this.editingEvent.endTime)

    // Combine date and time - use ISO 8601 format with timezone for K8s compatibility
    const startDateTime = startDateMoment.clone().set({
      hour: startTimeMoment.hours(),
      minute: startTimeMoment.minutes(),
      second: startTimeMoment.seconds()
    }).format() // ISO 8601 with timezone

    const endDateTime = endDateMoment.clone().set({
      hour: endTimeMoment.hours(),
      minute: endTimeMoment.minutes(),
      second: endTimeMoment.seconds()
    }).format() // ISO 8601 with timezone

    // Validate times
    if (moment(startDateTime).isAfter(moment(endDateTime))) {
      window.alert('Start time must be before end time!')
      return
    }

    const intervalData = {
      id: this.editingEvent.intervalId,
      sceneCollectionId: this.editingEvent.sceneCollectionId,
      interval_start: startDateTime,
      interval_end: endDateTime,
      priority: this.editingEvent.priority
    }

    this.controller._$log.info('[ScheduleCalendar] Saving interval:', intervalData)

    this.SceneCollectionService.editInterval(intervalData)
      .then(response => {
        this.controller._$log.info('[ScheduleCalendar] Interval saved successfully')
        this.controller.closeEventEditModal()
        // Calendar will update via WebSocket
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Error saving interval:', error)
        window.alert('Failed to save interval changes. Please check your connection and try again.')
      })
  }

  /**
     * Delete the current event
     */
  deleteEvent (event) {
    if (event) {
      this.editingEvent = event
    }
    if (!this.editingEvent || !this.editingEvent.intervalId) {
      return
    }

    if (!window.confirm('Are you sure you want to delete this interval?')) {
      return
    }

    this.controller._$log.info('[ScheduleCalendar] Deleting interval:', this.editingEvent.intervalId)

    this.SceneCollectionService.deleteInterval(this.editingEvent.intervalId)
      .then(response => {
        this.controller._$log.info('[ScheduleCalendar] Interval deleted successfully')
        this.controller.closeEventEditModal()
        // Calendar will update via WebSocket
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Error deleting interval:', error)
        window.alert('Failed to delete interval. Please check your connection and try again.')
      })
  }
}
